import { ArrowRight } from '@1debit/icons';
import { resourceToArticleShim } from 'components/ArticleGrid/utils';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import NavLink from 'components/shared/NavLink/NavLink';
import { getRichTextValue } from 'utils/formatters';
import { cn } from 'utils/styles';
import { generateIdSegmentFromLink } from './utils';
import { NavSubLinkProps } from './types';

interface NavNestedItemsListProps {
  subLink: NavSubLinkProps;
  idPrefix: string;
  className?: string;
  /** Section label above the list. Desktop only — mobile shows it in the back bar instead. */
  heading?: string;
}

const NavNestedItemsList = ({ subLink, idPrefix, className, heading }: NavNestedItemsListProps) => {
  const nestedItems = subLink.nestedItemsCollection?.items ?? [];
  const readAllIdSegment = generateIdSegmentFromLink(subLink.link);

  return (
    <div className={cn('flex flex-col', className)}>
      {heading && <p className="font-sans text-label-saans font-semibold mb-4">{heading}</p>}

      {nestedItems.map((item, index) => {
        if (!item) return null;

        const article = resourceToArticleShim(item);
        const categoryLabel = article.category?.label;
        const title = article.articleTitle;
        const plainTitle = title ? getRichTextValue(title) : '';
        const internalPage = article.href ? undefined : { slug: article.slug };
        const itemId = `${idPrefix}_${readAllIdSegment}_nested_${index}`;

        return (
          <div key={article.sys?.id ?? index}>
            <NavLink
              id={itemId}
              href={article.href}
              internalPage={internalPage}
              level={2}
              cleanLinkText={plainTitle}
              className="block group hover:text-current active:scale-none">
              {categoryLabel && (
                <p className="font-sans text-label-saans font-normal text-content-secondary mb-1">{categoryLabel}</p>
              )}
              <div className="font-sans text-heading-sm md:text-body-saans md:font-semibold group-hover:text-fern group-focus:text-fern">
                {title && <RichTextHeadline level="h3" headline={title} />}
              </div>
            </NavLink>
            <div className="border-t border-pebble my-5" />
          </div>
        );
      })}

      {subLink.link && (
        <NavLink
          id={`${idPrefix}_${readAllIdSegment}_read_all`}
          href={subLink.link.url}
          internalPage={subLink.link.internalPage}
          level={2}
          cleanLinkText="Read all"
          className="font-sans text-label-saans font-semibold w-fit">
          Read all <ArrowRight className="ml-2 inline" />
        </NavLink>
      )}
    </div>
  );
};

export default NavNestedItemsList;
